'use client';

import { useApi } from '@hooks/useApi';
import { useCallback } from 'react';

type ApiPayload = Parameters<ReturnType<typeof useApi>>[0];

/**
 * Awaitable wrapper around useApi.
 *
 * useApi's own promise settles only in its onError path, so `await api(...)` never returns on a
 * successful request. The capture flow is sequential — store the invoice, then link its payment,
 * then move on — so it needs a promise that settles either way.
 *
 * Resolves with the response body on success and with null on failure; any onSuccess/onFailure
 * the caller passes still runs first.
 */
export const useApiRequest = () => {
    const api = useApi();

    return useCallback(
        <T = any>(payload: ApiPayload): Promise<T | null> =>
            new Promise<T | null>((resolve) => {
                void api({
                    ...payload,
                    onSuccess: (data, response) => {
                        payload.onSuccess?.(data, response);
                        resolve(data as T);
                    },
                    onFailure: (error) => {
                        payload.onFailure?.(error);
                        resolve(null);
                    }
                });
            }),
        [api]
    );
};

export default useApiRequest;
